import React, { ReactNode } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import tinycolor from 'tinycolor2';

import { ConditionalWrapper } from 'shared/components/conditional-wrapper';
import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import usePaths from 'src/hooks/use-paths';
import { TierEarningBasis } from 'src/components/loyalty/rewards/dpos-utils';
import { useLoyaltyV1 } from './use-loyaltyV1';

type LoyaltyProps = {
  isWidgetLink?: boolean;
};

type ConditionalLinkWrapperProps = {
  isWidgetLink: boolean;
  url: string;
  children: ReactNode;
};

const ConditionalLinkWrapper = ({ isWidgetLink, url, children }: ConditionalLinkWrapperProps): JSX.Element => (
  <ConditionalWrapper condition={isWidgetLink} wrapper={(wrapperChildren) => <Link href={url}>{wrapperChildren}</Link>}>
    {children}
  </ConditionalWrapper>
);

export const LoyaltyV1 = ({ isWidgetLink = true }: LoyaltyProps): JSX.Element | null => {
  const {
    loyaltyConfig,
    loading,
    formatValueForDisplay,
    rewardsProgramName,
    balanceDaysToExpiration,
    badgeColor,
    currentTierName,
    currentTierThreshold,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    nextTierName,
    progressPercentage,
    isLoyaltyMember,
    isPoints,
    isDiscountRedemption,
  } = useLoyaltyV1();
  const { href: baseHref } = usePaths();

  if (loading || !loyaltyConfig || !loyaltyConfig.isEnabled || !isLoyaltyMember || !currentTierName) {
    return null;
  }

  const url = `${baseHref}/user/loyalty`;

  return (
    <ConditionalLinkWrapper isWidgetLink={isWidgetLink} url={url}>
      <Wrapper href={isWidgetLink ? url : null} as={!isWidgetLink && 'div'}>
        <DispoInfo>
          <Image>
            <DposLoyaltyIcon />
          </Image>

          <div>
            <Name>{rewardsProgramName}</Name>
            {badgeColor && currentTierName && <Tier $color={badgeColor}>{currentTierName}</Tier>}
          </div>
        </DispoInfo>

        {nextTierName && targetBalance && (
          <Meter
            role='meter'
            aria-valuemin={currentTierThreshold}
            aria-valuemax={targetBalance}
            aria-valuenow={currentYearTierBalance}
            aria-labelledby={`${rewardsProgramName} progress`}
          >
            <MeterLabel>{formatValueForDisplay(currentYearTierBalance, 'pts', isPoints)} spent</MeterLabel>
            {nextTierName && currentTierName && (
              <>
                <MeterProgress $width={progressPercentage} />
                <MeterLabel>
                  {formatValueForDisplay(targetBalance - currentYearTierBalance, 'pts', isPoints)} to {nextTierName}
                </MeterLabel>
              </>
            )}
          </Meter>
        )}

        <RewardInfo>
          <RewardAmount>
            {formatValueForDisplay(currentBalance, TierEarningBasis.points, isDiscountRedemption)}
          </RewardAmount>
          {currentBalance > 0 && (
            <RewardExpire>
              {balanceDaysToExpiration !== null ? (
                <>
                  {balanceDaysToExpiration && balanceDaysToExpiration > 0
                    ? `Expire in ${balanceDaysToExpiration} days`
                    : `${isDiscountRedemption ? 'Rewards points' : 'Rewards dollars'} expire today`}
                </>
              ) : (
                `Do not expire`
              )}
            </RewardExpire>
          )}
        </RewardInfo>
      </Wrapper>
    </ConditionalLinkWrapper>
  );
};

const DispoInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const Image = styled.div`
  border-radius: 50%;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  display: block;
  height: 56px;
  overflow: hidden;
  width: 56px;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: ${18 / 16};
`;

const Tier = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.customized.radius.tags};
  color: ${({ $color }) => (tinycolor($color).isDark() ? '#ffffff' : '#121516')};
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  margin-top: 6px;
  padding: 4px 6px;
  text-transform: uppercase;
`;

const Meter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin: 32px 0 20px;
`;

const MeterLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`;

const MeterProgress = styled.div<{ $width: string }>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()} 0%,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()}
      ${({ $width }) => $width},
    #fff ${({ $width }) => $width}
  );
  border-radius: ${({ theme }) => theme.customized.radius.sm};
  height: 8px;
  order: -1;
  position: relative;
  width: 100%;
`;

const RewardInfo = styled.div`
  align-items: center;
  background: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()};
  border-radius: ${({ theme }) => theme.customized.radius.md};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  line-height: 1;
  padding: 16px 12px;
`;

const RewardAmount = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const RewardExpire = styled.div`
  font-size: 12px;
`;

// This is our container query component
const Wrapper = styledContainerQuery.a`;
  background: ${({ theme }) => theme.customized.colors.buttonsLinks};
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  color: #fff;
  display: block;
  margin: 20px auto 40px;
  max-width: 1200px;
  overflow: hidden;
  padding: 20px;
  position: relative;
  width: 100%;
  z-index: 1;

  &::before {
    background: #ffffff1a;
    border-radius: 50%;
    content: '';
    height: 400px;
    left: 48px;
    pointer-events: none;
    position: absolute;
    top: 48px;
    transform: translate(-50%, -50%);
    width: 400px;
    z-index: -1;
  }

  &:container(min-width: 600px) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: space-between;

    ${Meter} {
      align-items: center;
      gap: 16px;
      margin: 0;
      order: 3;
      width: 100%;
    }

    ${MeterProgress} {
      flex: 1;
      order: unset;
      width: auto;
    }

    ${RewardInfo} {
      width: 264px;
    }
  }

  &:container(min-width: 900px) {
    gap: 32px;
    justify-content: flex-start;

    ${DispoInfo} {
      max-width: 218px;
    }

    ${Meter} {
      flex: 1;
      order: unset;
      width: auto;
    }
  }
`;
